import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { AuthProvider } from './lib/AuthContext';
import { LanguageProvider } from './lib/LanguageContext';
import { ResetPasswordForm } from './components/ResetPasswordForm.tsx';
import { PaymentSuccess } from './PaymentSuccess.tsx';
import { PaymentCancelled } from './PaymentCancelled.tsx';
import { TermsOfService } from './pages/TermsOfService.tsx';
import { PrivacyPolicy } from './pages/PrivacyPolicy.tsx';
import { OAuthConsent } from './pages/OAuthConsent.tsx';
import { initializeSentry } from './lib/sentry';
import { registerServiceWorker } from './lib/registerServiceWorker';
import './index.css';

// Initialize Sentry
initializeSentry();

// Register the PWA service worker (injectRegister is disabled in vite.config.ts
// so that registration failures are caught instead of surfacing as unhandled
// promise rejections)
registerServiceWorker();

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />
  },
  {
    path: "/reset-password",
    element: <ResetPasswordForm />
  },
  {
    path: "/payment-success",
    element: <PaymentSuccess />
  },
  {
    path: "/payment-cancelled",
    element: <PaymentCancelled />
  },
  {
    path: "/terms",
    element: <TermsOfService />
  },
  {
    path: "/privacy",
    element: <PrivacyPolicy />
  },
  {
    path: "/oauth/consent",
    element: <OAuthConsent />
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error }) => (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Something went wrong</h1>
          <p className="text-gray-700 mb-4">
            We're sorry, but something unexpected happened. Our team has been notified and we'll fix it soon.
          </p>
          <details className="mb-4">
            <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
              Error details
            </summary>
            <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
              {error.toString()}
            </pre>
          </details>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    )}>
      <LanguageProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </LanguageProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);